import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faTimes,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { Tooltip, Button, Space } from 'antd';
import SOSMultiSelectDropDownContainer from './styles/multi-select-checkbox-dropdown.styles';

/**
 * Multi-select dropdown component with checkboxes
 * Features: Select all, search, custom checkboxes, and tag display
 *
 * @param {Array} options - Array of option objects with label, value, and optional isSelected
 * @param {Array} defaultValue - Array of pre-selected values
 * @param {Function} handleChange - Callback function when selection changes
 * @param {String} placeholder - Placeholder text when no items selected
 * @param {Boolean} enableSearch - Enable/disable search functionality (default: true)
 * @param {Boolean} enableSelectAll - Enable/disable select all option (default: true)
 * @param {Boolean} requireAtLeastOne - Require at least one item to be selected (default: false)
 * @param {Number} maxLabelsToShow - Maximum number of labels to show before displaying count (default: 2)
 * @param {Boolean} enableApplyMode - Buffer changes until Apply is clicked; discard on outside close (default: false)
 * @param {Boolean} showClearAll - When apply mode is on, show the Clear All button in the footer (default: true)
 * @param {String} applyLabel - Label for the Apply button (default: 'Apply')
 * @param {String} clearAllLabel - Label for the Clear All button (default: 'Clear All')
 * @param {Number} autoApplyDelay - In apply mode, auto-commit the buffered selection this many ms after the last change (lets the user tick multiple checkboxes first); closing the dropdown also commits immediately instead of discarding. 0/undefined disables this (default: 0)
 * @return {JSX.Element} Multi-select dropdown component
 */
export const SOSMultiSelectCheckboxDropDown = ({
  options = [],
  defaultValue = [],
  handleChange,
  placeholder = 'Select options',
  enableSearch = true,
  enableSelectAll = true,
  requireAtLeastOne = false,
  maxLabelsToShow = 2,
  enableApplyMode = false,
  showClearAll = true,
  applyLabel = 'Apply',
  clearAllLabel = 'Clear All',
  autoApplyDelay = 0,
}) => {
  // Initialize selected values from defaultValue or options with isSelected flag
  const [selectedValues, setSelectedValues] = useState(() => {
    if (defaultValue.length > 0) {
      return defaultValue;
    }
    return options.filter((opt) => opt.isSelected).map((opt) => opt.value);
  });
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const ref = useRef(null);
  // Snapshot of the committed selection at the moment the dropdown opened;
  // used to revert on outside-close when enableApplyMode is on (and autoApplyDelay is off).
  const committedOnOpenRef = useRef(selectedValues);
  // Tracks the last defaultValue seen, to tell an explicit parent-driven clear (e.g. Clear All) apart from an options reload.
  const prevDefaultValueRef = useRef(defaultValue);
  // Debounce timer for autoApplyDelay; mirrors selectedValues so the outside-close listener (registered once per open) always commits the latest picks.
  const autoApplyTimerRef = useRef(null);
  const selectedValuesRef = useRef(selectedValues);

  useEffect(() => {
    selectedValuesRef.current = selectedValues;
  }, [selectedValues]);

  useEffect(() => () => clearTimeout(autoApplyTimerRef.current), []);

  // Commits the buffered selection to the parent immediately (used by both the debounce timer and close/Apply).
  const commitSelection = useCallback(
    (values) => {
      clearTimeout(autoApplyTimerRef.current);
      if (handleChange) handleChange(values);
      committedOnOpenRef.current = values;
    },
    [handleChange],
  );

  // requireAtLeastOne only locks toggles immediately in non-apply mode. In
  // apply mode changes are buffered, so we only enforce the constraint on Apply.
  const strictLock = requireAtLeastOne && !enableApplyMode;

  // Calculate all available values and check if all are selected
  const allValues = options.map((o) => o.value);
  const isAllSelected =
    options.length > 0 && selectedValues.length === options.length;

  // Synchronize component state when options or defaultValue change from parent
  useEffect(() => {
    const defaultValueChanged =
      JSON.stringify(prevDefaultValueRef.current) !==
      JSON.stringify(defaultValue);
    prevDefaultValueRef.current = defaultValue;

    if (defaultValue.length > 0) {
      setSelectedValues(defaultValue);
    } else if (defaultValueChanged) {
      // Parent explicitly cleared the selection (e.g. Clear All) — mirror it instead of falling back to options.isSelected.
      setSelectedValues([]);
    } else if (options.length > 0) {
      const selected = options
        .filter((opt) => opt.isSelected)
        .map((opt) => opt.value);
      if (selected.length > 0) {
        setSelectedValues(selected);
      }
    }
  }, [options, defaultValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return; // Only add listener when dropdown is open

    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        if (enableApplyMode) {
          if (autoApplyDelay > 0) {
            // Commit whatever is buffered instead of discarding, so closing the dropdown behaves like Apply.
            commitSelection(selectedValuesRef.current);
          } else {
            setSelectedValues(committedOnOpenRef.current);
          }
        }
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    // Small delay to prevent the opening click from immediately closing
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', close);
    }, 100);

    // Cleanup function to remove listener and clear timeout
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', close);
    };
  }, [isOpen, enableApplyMode, autoApplyDelay, commitSelection]);

  // Update internal state and notify parent of changes
  const update = (values) => {
    setSelectedValues(values);
    if (!enableApplyMode) {
      if (handleChange) handleChange(values);
      return;
    }
    // In apply mode, defer parent notification until Apply/close, unless autoApplyDelay lets the user finish picking first.
    if (autoApplyDelay > 0) {
      clearTimeout(autoApplyTimerRef.current);
      autoApplyTimerRef.current = setTimeout(() => {
        commitSelection(values);
      }, autoApplyDelay);
    }
  };

  const openDropdown = () => {
    committedOnOpenRef.current = selectedValues;
    setIsOpen(true);
  };

  const handleApply = () => {
    if (requireAtLeastOne && selectedValues.length === 0) return;
    commitSelection(selectedValues);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClearAll = () => {
    if (strictLock) return;
    setSelectedValues([]);
    if (!enableApplyMode) {
      if (handleChange) handleChange([]);
      return;
    }
    if (autoApplyDelay > 0) {
      clearTimeout(autoApplyTimerRef.current);
      autoApplyTimerRef.current = setTimeout(
        () => commitSelection([]),
        autoApplyDelay,
      );
    }
  };

  // Toggle individual option selection
  const toggleOption = (value) => {
    // Prevent deselecting if this is the last selected item (when requireAtLeastOne is true)
    if (
      strictLock &&
      selectedValues.includes(value) &&
      selectedValues.length === 1
    ) {
      return; // Do nothing, keep at least one selected
    }

    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    update(newValues);
  };

  // Toggle select all or deselect all
  const toggleSelectAll = () => {
    // Prevent deselecting all if all are currently selected (when requireAtLeastOne is true)
    if (strictLock && isAllSelected) {
      return; // Do nothing, keep at least one selected
    }
    const newValues = isAllSelected ? [] : allValues;
    update(newValues);
  };

  // Filter options based on search term
  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Generate display text for dropdown control based on selection
  const selectedLabels = options
    .filter((o) => selectedValues.includes(o.value))
    .map((o) => o.label);

  const titleText = (() => {
    if (selectedValues.length === 0) return null;
    if (isAllSelected) return 'All selected';
    if (selectedLabels.length <= maxLabelsToShow)
      return selectedLabels.join(', ');
    return `${selectedLabels.length} selected`;
  })();

  return (
    <SOSMultiSelectDropDownContainer ref={ref}>
      <div
        className={`dropdown-control ${isOpen ? 'open' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          if (isOpen) {
            if (enableApplyMode) {
              if (autoApplyDelay > 0) {
                commitSelection(selectedValues);
              } else {
                setSelectedValues(committedOnOpenRef.current);
              }
            }
            setIsOpen(false);
            setSearchTerm('');
          } else {
            openDropdown();
          }
        }}
      >
        <div className="dropdown-title">
          {titleText ? (
            titleText
          ) : (
            <span className="placeholder">{placeholder}</span>
          )}
        </div>
        <span className="dropdown-arrow">
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </span>
      </div>

      {isOpen && (
        <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
          <div className="dropdown-header">
            {enableSearch && (
              <div className="search-container">
                <input
                  className="search-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <span
                    className="search-clear"
                    onClick={() => setSearchTerm('')}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </span>
                )}
              </div>
            )}

            {enableSelectAll && (
              <Tooltip
                title={
                  strictLock && isAllSelected
                    ? 'At least one option must be selected'
                    : ''
                }
                placement="left"
              >
                <div
                  className="select-all-option option"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  {strictLock && isAllSelected ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '16px',
                        marginRight: '8px',
                        fontSize: '0.9rem',
                        color: '#6846d1',
                      }}
                    >
                      <FontAwesomeIcon icon={faExclamationCircle} size="lg" />
                    </span>
                  ) : (
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                    />
                  )}
                  <label
                    onClick={
                      strictLock && isAllSelected ? undefined : toggleSelectAll
                    }
                    style={{
                      cursor:
                        strictLock && isAllSelected ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Select All
                  </label>
                </div>
              </Tooltip>
            )}
          </div>

          <div className="options-container">
            {filteredOptions.length === 0 ? (
              <div className="no-options">No options found</div>
            ) : (
              filteredOptions.map((opt) => {
                const isChecked = selectedValues.includes(opt.value);
                const isLastSelected =
                  strictLock && isChecked && selectedValues.length === 1;

                return (
                  <Tooltip
                    key={opt.value}
                    title={
                      isLastSelected
                        ? `At least one option must be selected`
                        : ''
                    }
                    placement="left"
                  >
                    <div
                      className="option"
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      {isLastSelected ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            marginRight: '8px',
                            color: '#6846d1',
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faExclamationCircle}
                            size="lg"
                          />
                        </span>
                      ) : (
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleOption(opt.value)}
                        />
                      )}
                      <label
                        onClick={
                          isLastSelected
                            ? undefined
                            : () => toggleOption(opt.value)
                        }
                        style={{
                          cursor: isLastSelected ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {opt.label?.trim()}
                      </label>
                    </div>
                  </Tooltip>
                );
              })
            )}
          </div>

          {enableApplyMode && (
            <div className="dropdown-footer">
              <Space size="small">
                {showClearAll && (
                  <Button
                    size="small"
                    onClick={handleClearAll}
                    disabled={selectedValues.length === 0}
                  >
                    {clearAllLabel}
                  </Button>
                )}
                <Tooltip
                  title={
                    requireAtLeastOne && selectedValues.length === 0
                      ? 'At least one option must be selected'
                      : ''
                  }
                  placement="top"
                >
                  <Button
                    size="small"
                    type="primary"
                    onClick={handleApply}
                    disabled={requireAtLeastOne && selectedValues.length === 0}
                  >
                    {applyLabel}
                  </Button>
                </Tooltip>
              </Space>
            </div>
          )}
        </div>
      )}
    </SOSMultiSelectDropDownContainer>
  );
};

SOSMultiSelectCheckboxDropDown.propTypes = {
  options: PropTypes.array,
  defaultValue: PropTypes.array,
  handleChange: PropTypes.func,
  placeholder: PropTypes.string,
  enableSearch: PropTypes.bool,
  enableSelectAll: PropTypes.bool,
  requireAtLeastOne: PropTypes.bool,
  maxLabelsToShow: PropTypes.number,
  enableApplyMode: PropTypes.bool,
  showClearAll: PropTypes.bool,
  applyLabel: PropTypes.string,
  clearAllLabel: PropTypes.string,
  autoApplyDelay: PropTypes.number,
};
